import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import {
	getKanjiItem,
	getLearningMaterials,
	type MaterialEntry,
} from "~/lib/server/functions/learn";
import { getMaterialByLevelGrouped } from "~/lib/server/functions/learn";
import type { Level, Source } from "../server/db/schema/flashcard-schema";

export const materialQueryKeys = {
	vocabulary: (level: Level) => ["vocabulary", level] as const,
	grammar: (level: Level) => ["grammar", level] as const,
	kanji: (level: Level) => ["kanji", level] as const,
	kanjiDetail: (kanjiId: string) => ["kanji", "detail", kanjiId] as const,
	learningMaterials: ["learningMaterials"] as const,
};

export const learningMaterialsOptions = () =>
	queryOptions({
		queryKey: materialQueryKeys.learningMaterials,
		queryFn: getLearningMaterials,
	});

export function useLearningMaterials() {
	return useSuspenseQuery(learningMaterialsOptions());
}

export const vocabularyByLevelQueryOptions = (level: Level) =>
	queryOptions({
		queryKey: materialQueryKeys.vocabulary(level),
		queryFn: () =>
			getMaterialByLevelGrouped({
				data: { level, type: "vocabulary" },
			}) as Promise<MaterialEntry[][]>,
	});

export const grammarByLevelQueryOptions = (level: Level) =>
	queryOptions({
		queryKey: materialQueryKeys.grammar(level),
		queryFn: () =>
			getMaterialByLevelGrouped({
				data: { level, type: "grammar" },
			}) as Promise<MaterialEntry[][]>,
	});
export function useVocabularyByLevel(level: Level) {
	return useSuspenseQuery(vocabularyByLevelQueryOptions(level));
}

export const kanjiByLevelQueryOptions = (level: Level) =>
	queryOptions({
		queryKey: materialQueryKeys.kanji(level),
		queryFn: () =>
			getMaterialByLevelGrouped({
				data: { level, type: "kanji" },
			}) as Promise<MaterialEntry[][]>,
	});

export function useGrammarByLevel(level: Level) {
	return useSuspenseQuery(grammarByLevelQueryOptions(level));
}

export function useKanjiByLevel(level: Level) {
	return useSuspenseQuery(kanjiByLevelQueryOptions(level));
}

export const kanjiItemQueryOptions = (kanjiId: string) =>
	queryOptions({
		queryKey: materialQueryKeys.kanjiDetail(kanjiId),
		queryFn: () => getKanjiItem({ data: { kanjiId } }) as Promise<Source>,
	});

export function useKanjiItem(kanjiId: string) {
	return useSuspenseQuery(kanjiItemQueryOptions(kanjiId));
}
